<template>
    <div class="inline-edit-edit position-relative" v-if="isEditing">
        <slot name="edit" :model="model">
            <r-input
                auto-focus
                v-model="model.content"
                :multiline="multiline"
                :rows="multiline ? '3' : '1'"
                @keyup.enter.native="confirmEdit(true)"/>
        </slot>
        <slot name="editButtons" :model="model">
            <div class="btn-group position-absolute z-90" :style="buttonPositionStyle">
                <v-icon-button size="small" @click="confirmEdit">
                    <v-icon icon="check-s"/>
                </v-icon-button>
                <v-icon-button size="small" @click="cancelEdit">
                    <v-icon icon="close-s"/>
                </v-icon-button>
            </div>
        </slot>
    </div>
    <div class="position-relative"
         :class="{'inline-edit-display': !disabled, 'cursor-pointer': !disabled}"
         @mouseover="showEditIcon"
         @mouseleave="hideEditIcon"
         @click="enableEditing"
         v-else>
        <slot name="display" :model="model">
            <p :class="{'pre-wrap': multiline}">{{value | coverEmpty}}</p>
        </slot>
        <div class="position-absolute z-90"
             :style="editIconPositionStyle"
             @mouseover="showEditIcon"
             @mouseleave="hideEditIcon"
             v-if="isHoverDisplay">
            <v-icon icon="edit"/>
        </div>
    </div>
</template>

<script>
    import {
        vIconButton,
        vIcon,
    } from '@/components/form';

    export default {
        components: {
            vIconButton,
            vIcon,
        },
        props: {
            value: {
                required: true,
            },
            buttonsPosition: {
                type: String,
                default: 'bottomEnd',
                validator: position => [
                    'top',
                    'topStart',
                    'topEnd',
                    'topRight',
                    'topLeft',
                    'bottom',
                    'bottomStart',
                    'bottomEnd',
                    'bottomRight',
                    'bottomLeft',
                    'right',
                    'left',
                ].includes(position),
            },
            multiline: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        watch: {
            value: {
                immediate: true,
                handler(newValue) {
                    this.content = newValue;
                },
            },
        },
        computed: {
            model() {
                /**
                 * When accessing the model from a parent component keep in mind this flow as to properly
                 * manipulate the data.
                 * Value should be treated as readonly and is the source of truth; use this in custom #display
                 * Content is the manipulatable value; this should be readonly and used in custom #edit
                 *
                 * To make changes to content and value follow this flow,
                 * call update(newContent) to change the value of content.
                 * call confirm() to update value with content and emit the update event
                 * call cancel() to ignore any changes to content and toggle back to display mode
                 */
                return {
                    value: this.value,
                    content: this.content,
                    update: this.updateContent,
                    confirm: this.confirmEdit,
                    cancel: this.cancelEdit,
                };
            },
            editIconPositionStyle() {
                return {top: 0, right: 0};
            },
            buttonPositionStyle() {
                const margin = '4px';
                const buttonGroup = {
                    height: 28,
                    width: 54,
                };
                switch (this.buttonsPosition) {
                    case 'top':
                        return {bottom: `calc(100% + ${margin})`, left: `calc(50% - ${buttonGroup.width / 2}px)`};
                    case 'topStart':
                        return {bottom: `calc(100% + ${margin})`};
                    case 'topEnd':
                        return {bottom: `calc(100% + ${margin})`, left: `calc(100% - ${buttonGroup.width}px)`};
                    case 'topRight':
                        return {left: '100%', bottom: '100%'};
                    case 'topLeft':
                        return {right: '100%', bottom: '100%'};
                    case 'bottom':
                        return {top: `calc(100% + ${margin})`, left: `calc(50% - ${buttonGroup.width / 2}px)`};
                    case 'bottomStart':
                        return {top: `calc(100% + ${margin})`};
                    case 'bottomEnd':
                        return {top: `calc(100% + ${margin})`, left: `calc(100% - ${buttonGroup.width}px)`};
                    case 'bottomRight':
                        return {left: '100%', top: '100%'};
                    case 'bottomLeft':
                        return {right: '100%', top: '100%'};
                    case 'right':
                        return {bottom: `calc(50% - ${buttonGroup.height / 2}px)`, left: `calc(100% + ${margin})`};
                    case 'left':
                        return {bottom: `calc(50% - ${buttonGroup.height / 2}px)`, right: `calc(100% + ${margin})`};
                    default:
                        return {top: `calc(100% + ${margin})`, left: `calc(100% - ${buttonGroup.width}px)`};
                }
            },
        },
        data() {
            return {
                isEditing: false,
                isHoverDisplay: false,
                content: null,
                autoDismissEditIcon: null,
            };
        },
        methods: {
            enableEditing() {
                if (!this.disabled) {
                    this.isEditing = true;
                }
            },
            /**
             * Confirm editing and apply value. Use flag to trigger on enter
             * otherwise on click.
             * @param {boolean|Event} automatic
             */
            confirmEdit(automatic) {
                if (automatic === true && this.multiline) {
                    return;
                }
                this.isEditing = false;
                this.isHoverDisplay = false;
                this.updateContent(this.model.content);
                this.$emit('input', this.content);
            },
            cancelEdit() {
                this.isEditing = false;
                this.isHoverDisplay = false;
                this.content = this.value;
            },
            updateContent(content) {
                this.content = content;
            },
            showEditIcon() {
                if (this.disabled) {
                    return;
                }
                if (this.autoDismissEditIcon) {
                    clearInterval(this.autoDismissEditIcon);
                }
                this.autoDismissEditIcon = null;
                this.isHoverDisplay = true;
            },
            hideEditIcon() {
                this.isHoverDisplay = false;
            },
        },
    };
</script>
<docs>
    Inline Edit
    ```jsx static
    <template>
        <div>
            <div>
                <v-inline-edit v-model="example1" @input="save"/>
            </div>
            <div>
                <v-inline-edit v-model="example2" @input="save" button-position="topStart" icon-position="topStart">
                    <template #display="{model}">
                        <p class="pre-wrap">{{model.value}}</p>
                    </template>
                    <template #edit="{model}">
                        <textarea v-model="model.content" @input="model.update(model.content)" rows="4"></textarea>
                    </template>
                </v-inline-edit>
            </div>
        </div>
    </template>

    <script>
        export default {
            data() {
                return {
                    example1: 'This is example 1',
                    example2: 'This is example 4'
                };
            },
            method: {
                save() {
                    // some function to save the updated data.
                }
            }
        };
    </script>
    ```
</docs>
